import { useQueryClient } from 'react-query';
import { getSchemaQueryKey, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaV1Service, serviceName } from '../service';
import { MarkSiteBuildRangeAsUnpromotableIO, ServiceQueries } from '../types';
import { useV1Queries } from './use-v1-queries';
import { UseV1QueryEndpointArgs, useV1Query } from './use-v1-query';

/**
 * A hook that returns a query for the `MarkSiteBuildRangeAsUnpromotable` endpoint.
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useMarkSiteBuildRangeAsUnpromotableQuery = <E = unknown, D = MarkSiteBuildRangeAsUnpromotableIO['output']>(
  args: UseV1QueryEndpointArgs<'MarkSiteBuildRangeAsUnpromotable', E, D>
) =>
  useV1Query({
    endpointName: 'MarkSiteBuildRangeAsUnpromotable',
    ...args,
  });

/**
 * A hook that returns an array of queries for the `MarkSiteBuildRangeAsUnpromotable` endpoint.
 * It takes an array of objects with the following properties:
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useMarkSiteBuildRangeAsUnpromotableQueries = <
  E = unknown,
  D = MarkSiteBuildRangeAsUnpromotableIO['output']
>(
  argsArr: UseV1QueryEndpointArgs<'MarkSiteBuildRangeAsUnpromotable', E, D>[]
) =>
  useV1Queries(
    argsArr.map((args) => ({
      endpointName: 'MarkSiteBuildRangeAsUnpromotable',
      ...args,
    }))
  );

/**
 * A hook that returns a function to fetch the query for the `MarkSiteBuildRangeAsUnpromotable` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useFetchMarkSiteBuildRangeAsUnpromotable = () => {
  const queryClient = useQueryClient();

  const fetchMarkSiteBuildRangeAsUnpromotable = async <E = unknown, D = MarkSiteBuildRangeAsUnpromotableIO['output']>({
    request,
    options,
    httpOptions,
    requestKeysToOmitFromQueryKey,
    ...rest
  }: Omit<UseV1QueryEndpointArgs<'MarkSiteBuildRangeAsUnpromotable', E, D>, 'options'> & {
    options?: LimitedSchemaFetchQueryOptions<
      MarkSiteBuildRangeAsUnpromotableIO,
      E,
      D,
      ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'MarkSiteBuildRangeAsUnpromotable'>>
    >;
  }) => {
    const resolvedRequest = typeof request === 'function' ? request({}) : request;
    try {
      const response = await queryClient.fetchQuery<
        MarkSiteBuildRangeAsUnpromotableIO['output'],
        E,
        D,
        ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'MarkSiteBuildRangeAsUnpromotable'>>
      >(
        getSchemaQueryKey<ServiceQueries, 'MarkSiteBuildRangeAsUnpromotable'>({
          serviceName,
          endpointName: 'MarkSiteBuildRangeAsUnpromotable',
          request: resolvedRequest,
          httpOptions,
          requestKeysToOmitFromQueryKey,
        }),
        () => SchemaV1Service.MarkSiteBuildRangeAsUnpromotable(resolvedRequest, httpOptions),
        options
      );

      return response;
    } catch (error) {
      // We check it this way in case the fallbackDataOnError is nullish
      if ('fallbackDataOnError' in rest) {
        return rest.fallbackDataOnError as D;
      }
      throw error;
    }
  };

  return fetchMarkSiteBuildRangeAsUnpromotable;
};
